"use client"
// PROTOTYPE — throwaway. Variant B: "Split brand panel". See NOTES.md.
//
// The argument: for a vendor arriving cold from an invite email, this page is the
// first thing they ever see of Partnr. So half the screen does brand work — what
// this is, and why they're being asked to sign in — while the form keeps the
// conventional order (email first, work accounts as the alternative).

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { Check } from "lucide-react"
import { BRAND, BRAND_DARK, GoogleIcon, MicrosoftIcon, Spinner, type SignInViewProps } from "./shell"

const PROOF = [
  "Issue work requests to your private marketplace",
  "Receive and compare vendor proposals side by side",
  "Track contracts and obligations once work is awarded",
]

export function VariantB({
  email, setEmail, password, setPassword, loading, error, message, onSubmit, onOAuth,
}: SignInViewProps) {
  const field =
    "w-full rounded-lg border border-ui-border bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20"

  const oauthBtn =
    "flex items-center justify-center gap-2.5 rounded-lg border border-ui-border bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40"

  return (
    <div
      style={{ ["--brand" as string]: BRAND, ["--brand-dark" as string]: BRAND_DARK }}
      className="min-h-screen grid lg:grid-cols-2 bg-white"
    >
      {/* Brand half */}
      <div className="relative flex flex-col justify-between overflow-hidden bg-[var(--brand)] px-8 py-10 lg:px-14 lg:py-14">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-white/[0.06]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full bg-white/[0.04]"
        />

        <Image
          src="/partnr-wordmark.png"
          alt="Partnr"
          width={800}
          height={280}
          priority
          className="relative h-auto w-[136px] brightness-0 invert"
        />

        <div className="relative mt-10 lg:mt-0">
          <h2 className="max-w-[420px] text-[26px] font-bold leading-[1.25] text-white lg:text-[34px]">
            Procurement, from first brief to final obligation.
          </h2>
          <ul className="mt-8 space-y-4">
            {PROOF.map(item => (
              <li key={item} className="flex items-start gap-3 text-[14px] leading-6 text-white/85">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white/15">
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative mt-10 hidden text-[13px] text-white/50 lg:block">
          © {new Date().getFullYear()} Partnr
        </p>
      </div>

      {/* Form half */}
      <div className="flex items-center justify-center px-6 py-12 lg:px-14">
        <div className="w-full max-w-[380px]">
          <h1 className="text-[28px] font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-2 text-[14px] text-slate-500">Welcome back. Enter your details to continue.</p>

          {message && (
            <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-[13px] text-emerald-800">
              {message}
            </div>
          )}
          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            <div>
              <label htmlFor="b-email" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                Email
              </label>
              <input
                id="b-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={field}
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label htmlFor="b-password" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                Password
              </label>
              <input
                id="b-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={field}
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]/40 focus-visible:ring-offset-2"
            >
              {loading && <Spinner />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-ui-border" />
            <span className="text-[12px] text-slate-400">or continue with</span>
            <span className="h-px flex-1 bg-ui-border" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => onOAuth("google")} className={oauthBtn}>
              <GoogleIcon className="h-4 w-4" />
              Google
            </button>
            <button type="button" onClick={() => onOAuth("microsoft")} className={oauthBtn}>
              <MicrosoftIcon className="h-4 w-4" />
              Microsoft
            </button>
          </div>

          <p className="mt-8 text-[14px] text-slate-500">
            Don&apos;t have an account?{" "}
            <Link href="/auth/signup" className="font-semibold text-[var(--brand)] hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
